import { useEffect, useRef, useState } from 'react';

import { copyText } from '../utils/clipboard';

interface CopyButtonProps {
  value: string;
  /** Named in the tooltip and for screen readers, e.g. "EAN". */
  label: string;
}

/**
 * Copies one short value and says so for a moment.
 *
 * Rendered as a button rather than by making the text itself clickable: in the
 * product table the value sits inside a row-wide link, and a click that both
 * copies and navigates is a click that does neither on purpose.
 */
export function CopyButton({ value, label }: CopyButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  async function copy(event: React.MouseEvent) {
    // The table cell is a link; copying must not also open the product.
    event.preventDefault();
    event.stopPropagation();

    const ok = await copyText(value);

    setState(ok ? 'copied' : 'failed');
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState('idle'), 1400);
  }

  return (
    <button
      type="button"
      className={`copy${state === 'copied' ? ' copy--done' : ''}${state === 'failed' ? ' copy--failed' : ''}`}
      onClick={copy}
      title={state === 'failed' ? `Could not copy the ${label}` : `Copy ${label} ${value}`}
      aria-label={`Copy ${label} ${value}`}
    >
      <span aria-hidden="true">{state === 'copied' ? '✓' : state === 'failed' ? '✕' : '⧉'}</span>
      <span className="copy__text">{state === 'copied' ? 'Copied' : state === 'failed' ? 'Failed' : 'Copy'}</span>
    </button>
  );
}
